import { useState, useEffect, useRef } from 'react';
import { UnifiedCourseService } from '@/services/unifiedCourseService';
import { EnhancedDatabaseService } from '@/services/EnhancedDatabaseService';
import { Course } from '@/hooks/useCourses';
import { Module } from '@/types/courseTypes';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';

/**
 * Hook for unified course data loading with database-first approach and static fallback
 */
export const useUnifiedCourses = (category?: string) => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<'database' | 'static' | 'unknown'>('unknown');
  const [lastFetch, setLastFetch] = useState<number>(0);

  const fetchCourses = async (forceFresh = false) => {
    try {
      // Skip if fetched recently (within 5 seconds) unless forced
      const now = Date.now();
      if (!forceFresh && lastFetch && now - lastFetch < 5000) {
        return;
      }

      setLoading(true);
      setError(null);

      // Add cache-busting timestamp to force fresh data
      const data = category 
        ? await UnifiedCourseService.getCoursesByCategory(category)
        : await UnifiedCourseService.getAllCourses();

      setCourses(data);
      setLastFetch(now);
      
      // Determine source from metadata
      const firstCourse = data[0];
      const metadata = firstCourse?.metadata;
      if (typeof metadata === 'object' && metadata !== null && 'source' in metadata && metadata.source === 'static_fallback') {
        setSource('static');
      } else {
        setSource('database');
      }

      logger.info(`Loaded ${data.length} courses from ${source}${category ? ` for category ${category}` : ''}`);
    } catch (err) {
      logger.error('Error fetching unified courses:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch courses';
      setError(errorMessage);
      toast.error('Failed to load courses, showing available content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Use cached data for instant load - no aggressive clearing
    fetchCourses(false);
  }, [category]);

  return {
    courses,
    loading,
    error,
    source,
    refetch: fetchCourses
  };
};

export const useUnifiedCourse = (courseId: string) => {
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<'database' | 'static' | 'unknown'>('unknown');

  const fetchCourse = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await UnifiedCourseService.getCourseById(courseId);
      setCourse(data);
      
      if (data) {
        const metadata = data.metadata;
        const courseSource = (typeof metadata === 'object' && metadata !== null && 'source' in metadata && metadata.source === 'static_fallback') ? 'static' : 'database';
        setSource(courseSource);
        logger.info(`Loaded course: ${data.title} from ${courseSource}`);
      } else {
        logger.warn(`Course not found: ${courseId}`);
        setError('Course not found');
      }
    } catch (err) {
      logger.error('Error fetching unified course:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch course';
      setError(errorMessage);
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (courseId) {
      fetchCourse();
    }
  }, [courseId]);

  return {
    course,
    loading,
    error,
    source,
    refetch: fetchCourse
  };
};

export const useUnifiedCourseModules = (courseId: string) => {
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchModules = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await UnifiedCourseService.getCourseModules(courseId);
      setModules(data);
      logger.info(`Loaded ${data.length} modules for course ${courseId}`);
    } catch (err) {
      logger.error('Error fetching unified modules:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch modules';
      setError(errorMessage);
      toast.error('Failed to load course modules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (courseId) {
      fetchModules();
    }
  }, [courseId]);

  return {
    modules,
    loading,
    error,
    refetch: fetchModules
  };
};

export const useUnifiedModule = (moduleId: string, courseId: string) => {
  const [module, setModule] = useState<Module | null>(null);
  const [loading, setLoading] = useState(true);
  const [contentLoaded, setContentLoaded] = useState(false);
  const [fullyLoaded, setFullyLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cancelledRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const fetchModule = async () => {
    cancelledRef.current = false;

    // Safety timeout: force exit loading state after 8 seconds
    timeoutRef.current = setTimeout(() => {
      if (loading) {
        logger.warn(`⏱️ Module load timeout after 8s: ${moduleId}`);
        setError('Module loading timed out. Please retry.');
        setLoading(false);
      }
    }, 8000);

    try {
      setLoading(true);
      setContentLoaded(false);
      setFullyLoaded(false);
      setError(null);

      performance.mark('module-load-start');

      // PHASE 1 OPTIMIZATION: Progressive loading
      // STEP 1: Load shell (content only) - FAST (100-200ms target)
      try {
        const shell = await EnhancedDatabaseService.getModuleShell(moduleId, courseId);
        
        if (cancelledRef.current) return;

        if (shell) {
          setModule(shell);
          setContentLoaded(true); // ✅ User can read content NOW
          setLoading(false); // Stop spinner immediately
          
          performance.mark('content-visible');
          performance.measure('time-to-content', 'module-load-start', 'content-visible');
          const contentTime = performance.getEntriesByName('time-to-content')[0];
          logger.info(`✅ Content visible in ${Math.round(contentTime.duration)}ms`);

          // STEP 2: Enrich with examples, exercises - BACKGROUND (non-blocking)
          const enriched = await EnhancedDatabaseService.enrichModule(shell, moduleId, courseId);
          
          if (cancelledRef.current) return;
          
          setModule(enriched);
          setFullyLoaded(true); // ✅ All features available
          
          performance.mark('module-complete');
          performance.measure('time-to-complete', 'module-load-start', 'module-complete');
          const completeTime = performance.getEntriesByName('time-to-complete')[0];
          logger.info(`🎨 Full module loaded in ${Math.round(completeTime.duration)}ms`);

          // STEP 3: Prefetch next module - BACKGROUND
          EnhancedDatabaseService.prefetchAdjacentModules(courseId, moduleId);
        } else {
          throw new Error('Shell load returned null');
        }
      } catch (shellError) {
        // Fallback to complete module if shell fails
        logger.warn(`Shell load failed, using complete module for ${moduleId}`, shellError);
        
        if (cancelledRef.current) return;
        
        const data = await UnifiedCourseService.getCompleteModule(moduleId, courseId);
        
        if (cancelledRef.current) return;
        
        if (data) {
          setModule(data);
          setContentLoaded(true);
          setFullyLoaded(true);
          setLoading(false);
        } else {
          logger.error(`Module not found: ${moduleId} in course ${courseId}`);
          setError('Module not found');
          setLoading(false);
        }
      }
    } catch (err) {
      if (!cancelledRef.current) {
        logger.error('Error fetching unified module:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch module';
        setError(errorMessage);
        toast.error('Failed to load module');
        setLoading(false);
      }
    } finally {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  };

  useEffect(() => {
    if (moduleId && courseId) {
      fetchModule();
    }
    return () => {
      cancelledRef.current = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [moduleId, courseId]);

  return {
    module,
    loading,
    contentLoaded,
    fullyLoaded,
    error,
    refetch: fetchModule
  };
};

/**
 * Hook for performing system health checks
 */
export const useUnifiedSystemHealth = () => {
  const [health, setHealth] = useState<{
    database: boolean;
    static: boolean;
    recommended: 'database' | 'static';
  } | null>(null);
  const [loading, setLoading] = useState(false);

  const checkHealth = async () => {
    setLoading(true);
    try {
      const healthStatus = await UnifiedCourseService.healthCheck();
      setHealth(healthStatus);
      logger.info('System health check completed:', healthStatus);
    } catch (error) {
      logger.error('Health check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return {
    health,
    loading,
    checkHealth
  };
};